import { bpmsCompatible } from '@/audio/clipTiming';
import { fisherYates } from '@/components/Admin/ClassPackBuilder';
import { seatPackFingerprint } from './seatPacks';

const GROUPS = ['melody', 'drums', 'texture', 'fx'];
const LIMITS = { melody:20, drums:10, texture:10, fx:10 };
const idsOf = (pack) => GROUPS.flatMap((group) => (pack?.slots?.[group] || []).map((loop)=>String(loop.id)));
const compare = (left,right) => { for(let i=0;i<left.length;i++) { if(left[i]!==right[i]) return left[i]-right[i]; } return 0; };
function decode(entry) {
  try { const ids = typeof entry==='string' ? JSON.parse(entry) : entry?.ids;
    return Array.isArray(ids) ? {ids:[...new Set(ids.filter((id)=>typeof id==='string'))],bpm:entry?.bpm} : null;
  } catch {return null;}
}
export function expoVarietyNotice(packs) {
  const short = GROUPS.filter((group)=>new Set(packs.flatMap((pack)=>pack.slots[group].map((loop)=>loop.id))).size <= LIMITS[group]*2);
  return short.length ? `This event has a limited ${short.join(', ')} pool. Some sounds must repeat between visitors.` : '';
}

// Expo allocates continuously rather than restarting a class's one-seat schedule.
// Minimize reuse from this station's last kid, then the event's last kid, balance
// tempos and individual usage, and retain the existing musical tier preferences.
export function rotateExpoPack(packs, previous=[], {previousPack=null, random=Math.random}={}) {
  const catalog=Object.fromEntries(GROUPS.map((group)=>[group,[...new Map(packs.flatMap((pack)=>pack.slots[group]).map((loop)=>[String(loop.id),loop])).values()]]));
  if(GROUPS.some((group)=>catalog[group].length<LIMITS[group])) throw new Error('This Expo needs more tagged sounds before a full pack can be prepared.');
  const known=new Map(GROUPS.flatMap((group)=>catalog[group].map((loop)=>[String(loop.id),loop])));
  const history=previous.slice(-64).map(decode).filter(Boolean);
  const last=new Set(history[history.length-1]?.ids || []);
  const own=new Set(previousPack ? idsOf(previousPack) : last);
  const usage=new Map(),recency=new Map(),tempos=new Map();
  history.forEach((entry,index)=>{
    entry.ids.forEach((id)=>{usage.set(id,(usage.get(id)||0)+1);recency.set(id,index);});
    let bpm=entry.bpm;
    if(!Number.isFinite(Number(bpm))) {
      const counts=new Map();entry.ids.forEach((id)=>{const loop=known.get(id);if(loop?.instrument_group==='melody') counts.set(loop.bpm,(counts.get(loop.bpm)||0)+1);});
      bpm=[...counts].sort((a,b)=>b[1]-a[1])[0]?.[0];
    }
    if(bpm!=null)tempos.set(Number(bpm),(tempos.get(Number(bpm))||0)+1);
  });
  const seen=new Set(history.map((entry)=>JSON.stringify([...entry.ids].sort())));
  const pivots=[...new Map(packs.map((pack)=>[`${pack.bpm}:${pack.key || ''}`,{bpm:pack.bpm,key:pack.key}])).values()];
  let best=null,bestScore=null;
  for(const pivot of pivots) for(let attempt=0;attempt<4;attempt++) {
    const slots={},used=new Set();let tiers=0;
    for(const group of GROUPS) {
      const inKey=catalog[group].filter((loop)=>loop.key===pivot.key);
      const pool=group==='melody' && pivot.key && inKey.length>=LIMITS.melody ? inKey : catalog[group];
      const tier=(loop)=>(bpmsCompatible(loop.bpm,pivot.bpm)?0:4)+(Number(loop.bars)===8?0:2)+(group==='melody' && pivot.key && loop.key!==pivot.key?1:0);
      const rank=(loop)=>[own.has(String(loop.id))?1:0,last.has(String(loop.id))?1:0,usage.get(String(loop.id))||0,recency.get(String(loop.id)) ?? -1,tier(loop)];
      slots[group]=fisherYates(pool.filter((loop)=>!used.has(String(loop.id))),random).sort((a,b)=>compare(rank(a),rank(b))).slice(0,LIMITS[group]);
      slots[group].forEach((loop)=>{used.add(String(loop.id));tiers+=tier(loop);});
    }
    if(GROUPS.some((group)=>slots[group].length!==LIMITS[group])) continue;
    const candidate={id:'expo-pack',name:'Your Expo soundpack',bpm:pivot.bpm,key:slots.melody[0]?.key || pivot.key,slots};
    // Least-recent ordering can cycle whole cohorts together. Break a repeated
    // full combination with one available alternative without increasing reuse
    // from this station's previous visitor or changing the melody key pool.
    if(seen.has(seatPackFingerprint(candidate))) {
      let changed=false;
      for(const group of GROUPS) {
        const inKey=catalog[group].filter((loop)=>loop.key===pivot.key);
        const pool=group==='melody' && pivot.key && inKey.length>=LIMITS.melody ? inKey : catalog[group];
        const index=slots[group].length-1,old=slots[group][index];
        for(const replacement of fisherYates(pool,random)) {
          if(used.has(String(replacement.id)) || Number(own.has(String(replacement.id)))>Number(own.has(String(old.id)))) continue;
          slots[group][index]=replacement;
          if(!seen.has(seatPackFingerprint(candidate))) {changed=true;break;}
          slots[group][index]=old;
        }
        if(changed)break;
      }
    }
    const ids=idsOf(candidate);
    const score=[ids.filter((id)=>own.has(id)).length,seen.has(seatPackFingerprint(candidate))?1:0,ids.filter((id)=>last.has(id)).length,tempos.get(Number(pivot.bpm))||0,ids.reduce((sum,id)=>sum+(usage.get(id)||0),0),ids.reduce((sum,id)=>sum+(recency.get(id) ?? -1),0),tiers,random()];
    if(!best || compare(score,bestScore)<0) {best=candidate;bestScore=score;}
  }
  if(!best)throw new Error('This Expo needs more distinct tagged sounds to prepare a full pack.');
  return best;
}
